import { UpperCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { WhiteLabelService } from '@flexyoo/common';
import type { EmployeeSelectedBenefit, IEmployeeCartArchiveItem } from '@flexyoo/employee/models';
import {
  EmployeeCartSection,
  getBenefitDisplayAmount,
  getEmployeeCartArchiveActionPresentation,
  getEmployeeCartArchiveBenefitSections,
  getEmployeeCartArchivePresentation,
  getEmployeeCartArchiveStatusColor,
  shouldShowArchiveConfirmAndResign,
} from '@flexyoo/employee/util';
import { FlexyooNumberPipe } from '@flexyoo/shared/pipes';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'flexyoo-employee-basket-cart-archive-item',
  templateUrl: './employee-basket-cart-archive-item.component.html',
  styleUrl: './employee-basket-cart-archive-item.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    UpperCasePipe,
    TranslateModule,
    MatIconModule,
    MatExpansionModule,
    MatButtonModule,
    MatTooltip,
    FlexyooNumberPipe,
  ],
})
export class EmployeeBasketCartArchiveItemComponent {
  protected readonly wls = inject(WhiteLabelService);

  readonly cart = input.required<IEmployeeCartArchiveItem>();
  readonly signCart = output<number>();
  readonly signBenefit = output<EmployeeSelectedBenefit>();

  protected readonly presentation = computed(() => {
    const cart = this.cart();
    return getEmployeeCartArchivePresentation(
      cart.CartState,
      cart.LastCartStateBeforeExpired,
      cart.EmployeeBenefits,
      cart.documents
    );
  });

  protected readonly actionPresentation = computed(() => {
    const cart = this.cart();
    return getEmployeeCartArchiveActionPresentation(
      cart.CartState,
      cart.EmployeeBenefits,
      cart.documents,
      cart.LastCartStateBeforeExpired
    );
  });

  protected readonly sections = computed(() => {
    const cart = this.cart();
    return getEmployeeCartArchiveBenefitSections(
      cart.CartState,
      cart.EmployeeBenefits,
      cart.LastCartStateBeforeExpired
    );
  });

  protected readonly benefitCount = computed(() =>
    this.sections().reduce((count, section) => count + section.benefits.length, 0)
  );

  protected readonly showConfirmAndResign = computed(() => {
    const cart = this.cart();
    return shouldShowArchiveConfirmAndResign(
      cart.CartState,
      cart.EmployeeBenefits,
      cart.documents,
      cart.LastCartStateBeforeExpired
    );
  });

  protected readonly isConfirmAndResignDisabled = computed(() => {
    const cart = this.cart();
    return (
      !cart.Id ||
      cart.EmployeeBenefits.some((benefit) => benefit.hasInsufficientBudget) ||
      this.actionPresentation().isConfirmAndSignDisabled
    );
  });

  protected readonly isOutsideChoicePeriod = computed(() => !this.cart().TenantGroupPeriodId);

  protected readonly statusColor = computed(() => {
    const presentation = this.presentation();
    return getEmployeeCartArchiveStatusColor(presentation.kind, {
      primaryColor: this.wls.primaryColor,
      warnColor: this.wls.warnColor,
    });
  });

  protected sectionDotColor(section: EmployeeCartSection): string {
    switch (section.color) {
      case 'primary':
        return this.wls.primaryColor;
      case 'warn':
        return this.wls.warnColor;
      case 'accent':
        return this.wls.accentColor;
      case 'cancelled':
        return 'var(--flexyoo-impersonated-banner-color)';
    }
  }

  protected sectionCountBackgroundColor(section: EmployeeCartSection): string {
    switch (section.color) {
      case 'primary':
        return this.wls.primaryColor;
      case 'warn':
        return this.wls.warnColor;
      case 'accent':
        return this.wls.accentColor;
      case 'cancelled':
        return 'var(--flexyoo-impersonated-banner-color)';
    }
  }

  protected sectionCountTextColor(section: EmployeeCartSection): string {
    switch (section.color) {
      case 'primary':
        return this.wls.primaryContrastingColor;
      case 'warn':
        return this.wls.warnContrastingColor;
      case 'accent':
        return this.wls.primaryContrastingColor;
      case 'cancelled':
        return '#ffffff';
    }
  }

  protected displayAmount(benefit: EmployeeSelectedBenefit): number {
    return getBenefitDisplayAmount(benefit);
  }

  protected rejectedOfferReason(
    section: EmployeeCartSection,
    benefit: EmployeeSelectedBenefit
  ): string | null {
    return section.showReuploadWarning ? (benefit.offerDisapprovedReason ?? null) : null;
  }

  protected onReuploadBenefit(benefit: EmployeeSelectedBenefit, event: MouseEvent): void {
    event.stopPropagation();
    this.signBenefit.emit(benefit);
  }

  protected onSignCart(): void {
    const cartId = this.cart().Id;
    if (!cartId || this.isConfirmAndResignDisabled()) {
      return;
    }

    this.signCart.emit(cartId);
  }
}
